import { RootState } from "@base/store";
import { WithAnalytics } from "@base/utils/hoc/withSendPageAnalytics";
import Button from "@components/atoms/button";
import Chip from "@components/atoms/chip";
import GameDetailsCard from "@components/molecules/game-details-card";
import Header from "@components/molecules/header";
import Layout from "@components/molecules/layout";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import { Box, Dialog, Divider, Snackbar } from "@mui/material";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import * as React from "react";
import { useEffect, useState } from "react";
import { isBrowser, isMobile } from "react-device-detect";
import { ConnectedProps, connect, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { WhatsappShareButton } from "react-share";
import { ReactComponent as Share } from "@assets/images/share/share.svg";

import { gameDetailsActions } from "../game-details/gameDetails.slice";
import { gameActions } from "../game/game.slice";
import { GameProps, PlayerProps, WaitlistProps } from "../game/game.type";
import { TransactionProps } from "../transactions/transaction.type";
import { transactionsActions } from "../transactions/transactions.slice";
import {
  Avatar,
  Content,
  DesktopShareDialog,
  DesktopShareTitle,
  OptionWithDividerWrapper,
  OptionWithIconWrapper,
  OrganizerLabel,
  PlayerInfoWrapper,
  PlayerName,
  PlayerWrapper,
  PlayersContainer,
  ShareActions,
  Team,
  TeamDescription,
  TeamHeader,
  TeamMembers,
  TeamTitle,
  Teams,
  TeamsWrapper,
  Title,
  UserViewChipWrapper,
} from "./publicGame.styled";

import { ReactComponent as Copy } from "@assets/images/copy/copyIcon.svg";
import { ReactComponent as Whatsapp } from "@assets/images/whatsApp/whatsapp.svg";

type ReduxProps = ConnectedProps<typeof connector>;

const PublicGame = ({
  getGameById,
  rsvpToAGame,
  reset,
  setAlert,
  leaveWaitList,
  userHasStripeAccount,
  getPaymentsIntents,
}: ReduxProps) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { REACT_APP_DEV } = process.env;
  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);

  const userId = useSelector((state: RootState) => state.user.user.uuid);
  const [selectedGame, setSelectedGame] = useState<GameProps>();
  const [isAttending, setIsAttending] = useState<boolean>(false);
  const [urlCopied, setIsUrlCopied] = useState<boolean>(false);
  const [isDesktopShareOpen, setIsDesktopShareOpen] = useState<boolean>(false);
  const [hasStripeAccount, setHasStripeAccount] = useState<boolean | null>(
    null
  );
  const [paymentStatus, setPaymentStatus] = useState(false);
  const [intentId, setIntentId] = useState<string>("");
  const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
  });

  const handleTooltipClose = () => {
    setIsDesktopShareOpen(false);
  };

  const handleTooltipOpen = () => {
    setIsDesktopShareOpen(true);
  };

  const f = (inputDate: string) => {
    const formattedDate = dayjs(inputDate).format("h:mm A, D MMM, YYYY");

    return formattedDate;
  };

  const HasUserStripeAccount = async () => {
    const response = await userHasStripeAccount();
    switch (response?.payload?.details_submitted) {
      case false:
        setHasStripeAccount(false);

        break;
      case true:
        setHasStripeAccount(true);
        break;
      case undefined:
        setHasStripeAccount(false);
    }
  };

  const handleDateTime = (inputDate: string) => {
    const formattedDate = dayjs(inputDate).format("h:mm A, DD/MM/YY");

    return formattedDate;
  };

  const handleBack = () => {
    reset();
    navigate("/home");
  };

  const handleOnShare = async () => {
    if (isMobile) {
      handleTooltipOpen();

      const shareData = {
        title: "Rkbt",
        text: "join our game!",
        url: `${REACT_APP_DEV}/game-details/${id}`,
      };
      if (navigator.share) {
        await navigator.share(shareData);
      }
    }
    if (isBrowser) {
      handleTooltipOpen();
    }
  };

  const hasPaid = async () => {
    const response = await getPaymentsIntents(id!);
    if (response?.meta?.requestStatus === "fulfilled") {
      response?.payload?.forEach((trx: TransactionProps) => {
        if (trx.status === "succeeded") {
          setPaymentStatus(true);
          setIntentId(trx?.id);
        }
      });
    }
  };

  const findWaitlistIdByUserId = (userIdToFind: string) => {
    const foundUser = selectedGame?.waitlist?.find(
      (item: WaitlistProps) => item.user.id === userIdToFind
    );

    if (foundUser) {
      return foundUser.id;
    }
  };

  const handleLeaveWaitList = async () => {
    const waitlistId = findWaitlistIdByUserId(userId!);
    if (waitlistId) {
      const response = await leaveWaitList(waitlistId);
      setSelectedGame(response?.payload);
    }
  };

  const DesktopShare = () => {
    return (
      <Dialog
        open={isDesktopShareOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleTooltipClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DesktopShareDialog>
          <DesktopShareTitle>
            {"Invite your friends to this game!"}
          </DesktopShareTitle>
          <ShareActions>
            <Button
              fontFamily="NotoSans-SemiBold"
              fontSize="14px"
              textColor={"#FFF"}
              text={"Copy Link"}
              endIcon={<Copy />}
              color="secondary"
              type={"button"}
              onClick={() => {
                navigator.clipboard.writeText(
                  `${REACT_APP_DEV}/game-details/${selectedGame?.id}`
                );
                setIsUrlCopied(true);
              }}
            />
            <WhatsappShareButton
              style={{ width: "100%" }}
              url={`${REACT_APP_DEV}/game-details/${selectedGame?.id}`}
              title={`${selectedGame?.title}`}
              separator=""
            >
              <Button
                fontFamily="NotoSans-SemiBold"
                fontSize="14px"
                textColor={"#FFF"}
                text={"Share Game via WhatsApp"}
                endIcon={<Whatsapp />}
                color="secondary"
                type={"button"}
                onClick={() => {}}
              />
            </WhatsappShareButton>
          </ShareActions>
        </DesktopShareDialog>
      </Dialog>
    );
  };

  const isUserAPlayer = (game: GameProps, id: string) => {
    let isPlayer = false;

    game.sides.forEach((side) => {
      const player = side.players.find(
        (player) => player?.rsvp?.user?.id === id
      );
      if (player) {
        isPlayer = true;
      }
    });
    setIsAttending(isPlayer);
    return isPlayer;
  };

  const isUserASub = !!selectedGame?.waitlist?.some(
    (entry: any) => entry.user.id === userId
  );

  const handleRSVP = async (status: "N" | "A") => {
    const body = {
      status,
    };
    if (selectedGame?.id && userId) {
      const response = await rsvpToAGame({ id: selectedGame?.id, body });
      if (response?.meta.requestStatus === "fulfilled") {
        isUserAPlayer(response?.payload?.game, userId);
        setSelectedGame(response?.payload?.game);
        if (!response?.payload?.success) {
          setAlert({
            alert: {
              isVisible: true,
              message: `${response?.payload?.message}`,
              type: "error",
            },
          });
        }
      }
    }
  };

  const getAdmin = () => {
    const adminId = selectedGame?.game_admins[0]?.user?.id;
    return adminId;
  };

  const isOrganizer = (playerId: string) => {
    return getAdmin() === playerId;
  };

  const waitlistUserViewRender = (players: WaitlistProps[]) => {
    return (
      <>
        {players.length > 0 ? (
          <PlayersContainer>
            {players.map((player, index) => (
              <PlayerInfoWrapper key={index}>
                <OptionWithDividerWrapper>
                  <OptionWithIconWrapper>
                    <PlayerWrapper>
                      <Avatar
                        alt="user Photo"
                        src={player?.user?.profile_url}
                        {...stringAvatar(
                          `${player?.user?.first_name} ${player?.user?.last_name}`
                        )}
                      />
                      {isOrganizer(player?.user?.id) ? (
                        <OrganizerLabel>{`${player?.user?.first_name} ${player?.user?.last_name} (Organizer)`}</OrganizerLabel>
                      ) : (
                        <PlayerName>{`${player?.user?.first_name} ${player?.user?.last_name}`}</PlayerName>
                      )}
                    </PlayerWrapper>
                  </OptionWithIconWrapper>
                  {index === players.length && (
                    <Divider orientation="horizontal" />
                  )}
                </OptionWithDividerWrapper>
              </PlayerInfoWrapper>
            ))}
          </PlayersContainer>
        ) : (
          <TeamDescription>{"No one on the waitlist yet."}</TeamDescription>
        )}
      </>
    );
  };

  const TeamRender = (title: string, players: PlayerProps[]) => {
    return (
      <Team>
        <TeamHeader>
          <TeamTitle>{title}</TeamTitle>
          <TeamMembers>{players.length}</TeamMembers>
        </TeamHeader>
        {players.length > 0 ? (
          <PlayersContainer>
            {players.map((player, index) => (
              <OptionWithDividerWrapper key={index}>
                <OptionWithIconWrapper>
                  <PlayerWrapper>
                    <Avatar
                      alt="user Photo"
                      {...stringAvatar(
                        `${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name}`
                      )}
                    />
                    {isOrganizer(player?.rsvp?.user?.id) ? (
                      <OrganizerLabel>{`${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name} (Organizer)`}</OrganizerLabel>
                    ) : (
                      <PlayerName>{`${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name}`}</PlayerName>
                    )}
                  </PlayerWrapper>
                  <UserViewChipWrapper>
                    {!isOrganizer(player?.rsvp?.user?.id) && (
                      <Chip
                        label={player?.rsvp?.paid ? "paid" : "Unpaid"}
                        color={player?.rsvp?.paid ? "#36BBA0" : "#8D59D1"}
                        bgColor={
                          player?.rsvp?.paid
                            ? " rgba(54, 187, 160, 0.13)"
                            : "#F0E5FF"
                        }
                      />
                    )}
                  </UserViewChipWrapper>
                </OptionWithIconWrapper>
                {index === players.length && (
                  <Divider orientation="horizontal" />
                )}
              </OptionWithDividerWrapper>
            ))}
          </PlayersContainer>
        ) : (
          <TeamDescription>
            {
              "No one in this team yet. Invite more players to see more people in this list."
            }
          </TeamDescription>
        )}
      </Team>
    );
  };

  const waitlistRender = (
    title: string,
    players: WaitlistProps[],
    side: "Waitlist"
  ) => {
    return (
      <Team>
        <TeamHeader>
          <TeamTitle>{title}</TeamTitle>
          <TeamMembers>{players.length}</TeamMembers>
        </TeamHeader>
        {waitlistUserViewRender(players)}
      </Team>
    );
  };

  const stringAvatar = (name: string) => {
    return {
      children: `${name?.split(" ")[0][0]?.toUpperCase()}${name
        ?.split(" ")[1][0]
        ?.toUpperCase()}`,
    };
  };

  const handleGame = async () => {
    if (id) {
      const response = await getGameById({ id, isAuth: false });
      if (response.meta.requestStatus === "fulfilled") {
        setSelectedGame(response.payload);
      }
    }
  };

  const setSharedGameId = () => {
    if (id) {
      localStorage.setItem("sharedGameId", id);
    }
  };

  useEffect(() => {
    handleGame();
    HasUserStripeAccount();
    hasPaid();
  }, []);

  useEffect(() => {
    if (urlCopied) {
      handleTooltipClose();
    }
  }, [urlCopied]);

  useEffect(() => {
    if (selectedGame && userId) {
      isUserAPlayer(selectedGame, userId);
    }
  }, [selectedGame, userId]);

  return (
    <Layout
      isLayout={true}
      HeaderChildren={
        <Header
          title=""
          // icon={<Share />}
          // handleClick={handleOnShare}
          handleBack={handleBack}
        />
      }
    >
      {
        <>
          {selectedGame?.id && (
            <>
              <GameDetailsCard
                isAttending={isAttending}
                handleRSVP={handleRSVP}
                paymentStatus={paymentStatus}
                isUserASub={isUserASub}
                currency={selectedGame?.currency}
                maxPlayers={selectedGame?.max_players}
                gameStatus="upcoming"
                currentPlayersQty={
                  selectedGame?.sides[0].players.length +
                  selectedGame?.sides[1].players.length
                }
                selectedGame={id!}
                hasStripeAccount={hasStripeAccount}
                handleLeaveWaitList={handleLeaveWaitList}
                handelShare={handleOnShare}
                startDate={selectedGame?.start_date}
                endDate={selectedGame?.end_date}
                setSharedGameId={setSharedGameId}
                date={handleDateTime(selectedGame?.start_date)}
                title={selectedGame?.title}
                link={selectedGame?.chat_link}
                intentId={intentId}
                isOrganizer={isOrganizer(userId!)}
                info={selectedGame?.description}
                location={selectedGame?.location}
                fee={selectedGame?.fee}
                isCash={selectedGame?.accept_cash_payment!}
                isOnlineTransfer={selectedGame?.accept_online_payment!}
                isOnlinePayment={selectedGame?.accept_app_payment!}
              />
              <Content>
                <TeamsWrapper>
                  <Title>{`Players (${
                    selectedGame?.sides[0].players.length +
                    selectedGame?.sides[1].players.length
                  }/${selectedGame?.max_players})`}</Title>
                  <Teams>
                    {selectedGame?.sides.map((game, index) => {
                      return (
                        <Box key={index}>
                          {TeamRender(game?.side_name, game?.players)}
                          {index === 0 && (
                            <>
                              <br />
                              <Divider orientation="horizontal" />
                            </>
                          )}
                        </Box>
                      );
                    })}
                    <Box>
                      {waitlistRender(
                        "Waitlist",
                        selectedGame?.waitlist || [],
                        "Waitlist"
                      )}
                    </Box>
                  </Teams>
                </TeamsWrapper>
                <Snackbar
                  open={urlCopied}
                  autoHideDuration={3000}
                  onClose={() => setIsUrlCopied(false)}
                  message="Copied To Clipboard!"
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                />
              </Content>
            </>
          )}
          <DesktopShare />
        </>
      }
    </Layout>
  );
};

const mapDispatchToProps = {
  getGameById: gameDetailsActions.getGameById,
  rsvpToAGame: gameDetailsActions.rsvpToAGame,
  reset: gameDetailsActions.reset,
  setAlert: LoaderSnackbarWrapperActions.setGlobalAppearances,
  leaveWaitList: gameActions.leaveWaitList,
  userHasStripeAccount: transactionsActions.userHasStripeAccount,
  getPaymentsIntents: transactionsActions.getPaymentsIntentsByGameId,
};

const connector = connect(undefined, mapDispatchToProps);
const PublicGameRedux = connector(WithAnalytics(PublicGame));

export { PublicGameRedux as PublicGame };
